import React, { useState, useEffect } from "react";
import Navbar from "../Navbar";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faBars, faTimes } from "@fortawesome/free-solid-svg-icons";
import { useSubmitCmpBetaFeedbackMutation } from "../../graphql/types";
import { useLocation } from "react-router-dom";
import { RoutePaths } from "../../enumStrings";

type LayoutProps = {
  children: React.ReactNode;
};

const Layout: React.FC<LayoutProps> = ({ children }) => {
  const [isNavbarOpen, setIsNavbarOpen] = useState(true);
  const [isMobile, setIsMobile] = useState(false);
  const [isFeedbackModalOpen, setIsFeedbackModalOpen] = useState(false);
  const [feedbackText, setFeedbackText] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState("");
  const location = useLocation();

  const [submitCmpBetaFeedbackMutation] = useSubmitCmpBetaFeedbackMutation();

  // Check if device is mobile on mount and window resize
  useEffect(() => {
    const checkIfMobile = () => {
      const isMobileView = window.innerWidth < 768;
      setIsMobile(isMobileView);
      setIsNavbarOpen(!isMobileView);
    };

    // Initial check
    checkIfMobile();

    // Add event listener
    window.addEventListener("resize", checkIfMobile);

    // Clean up
    return () => window.removeEventListener("resize", checkIfMobile);
  }, []);

  // Block main-content scrolling when overlay is active on the page
  const shouldBlockScroll = (() => {
    const normalizePath = (path: string) => {
      const trimmed = path.replace(/\/+$/, "");
      return trimmed.length === 0 ? "/" : trimmed;
    };

    const currentPath = normalizePath(location.pathname);

    const excludedPaths: string[] = [
      // common pages
      RoutePaths.error500,
      RoutePaths.login,
      RoutePaths.logout,
      RoutePaths.newPassword,
      RoutePaths.forgotPassword,
      RoutePaths.home,
      // specified exceptions
      RoutePaths.chamberAdmin_myChamber_manageChamber,
      // RoutePaths.chamberAdmin_myChamber_analytics,
      // RoutePaths.chamberAdmin_support,
    ];
    const excludedSet = new Set(excludedPaths.map(normalizePath));
    return !excludedSet.has(currentPath);
  })();

  const handleToggleNavbar = () => {
    setIsNavbarOpen(!isNavbarOpen);
  };

  const handleOpenFeedbackModal = () => {
    setIsFeedbackModalOpen(true);
    setSubmitMessage(""); // Clear any previous messages
  };

  const handleCloseFeedbackModal = () => {
    setIsFeedbackModalOpen(false);
    setFeedbackText("");
    setIsSubmitting(false);
    setSubmitMessage("");
  };

  const handleSubmitFeedback = async () => {
    if (!feedbackText.trim()) return;

    setIsSubmitting(true);
    setSubmitMessage("");

    try {
      const result = await submitCmpBetaFeedbackMutation({
        variables: {
          feedback: feedbackText.trim(),
          urlPath: window.location.pathname,
        },
      });

      if (result.data?.submitCMPBetaFeedback?.success) {
        setSubmitMessage(result.data.submitCMPBetaFeedback.message);
        setTimeout(() => {
          handleCloseFeedbackModal();
        }, 2000);
      } else {
        setSubmitMessage(
          result.data?.submitCMPBetaFeedback?.message ||
            "Failed to submit feedback. Please try again.",
        );
      }
    } catch (error) {
      console.error("Error submitting feedback:", error);
      setSubmitMessage("Failed to submit feedback. Please try again later.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="flex h-screen bg-white relative overflow-hidden">
      {/* Mobile navbar overlay */}
      {isMobile && isNavbarOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-20"
          onClick={() => setIsNavbarOpen(false)}
          aria-label="Close navigation menu"
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              setIsNavbarOpen(false);
            }
          }}
        />
      )}

      {/* Navbar */}
      <div
        className={`${
          isNavbarOpen ? "translate-x-0" : "-translate-x-full"
        } transition-transform duration-300 ease-in-out fixed md:relative z-30 h-screen`}
      >
        <Navbar onClose={() => setIsNavbarOpen(false)} />
      </div>

      {/* Main content */}
      <div
        className={`${
          shouldBlockScroll ? "overflow-hidden" : "overflow-auto"
        } flex-1 z-10 transition-all duration-300`}
      >
        {/* Mobile toggle button */}
        <button
          className="md:hidden fixed top-4 left-4 z-40 bg-white p-2 rounded-full shadow-md hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-gray-300"
          onClick={handleToggleNavbar}
          aria-label="Toggle navigation menu"
          tabIndex={0}
        >
          <FontAwesomeIcon icon={faBars} className="text-gray-700" />
        </button>

        {/* Beta Feedback button - bottom right with platform colors */}
        <button
          className="fixed bottom-6 right-6 z-40 text-white px-4 py-3 rounded-full shadow-lg focus:outline-none focus:ring-2 transition-all duration-200 text-sm font-bold"
          style={{
            backgroundColor: "#E1A731",
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = "#c78f1e";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = "#E1A731";
          }}
          onClick={handleOpenFeedbackModal}
          aria-label="Send beta feedback"
          tabIndex={0}
          title="Beta Feedback"
        >
          BETA FEEDBACK
        </button>

        {/* Content with padding */}
        <div
          className={`md:pt-4 ${
            isMobile ? "pt-16" : ""
          } md:ml-0 w-full relative min-h-screen`}
        >
          {children}
        </div>
      </div>

      {/* Feedback Modal - moved to root level to cover everything */}
      {isFeedbackModalOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4"
          style={{ zIndex: 9999 }}
        >
          <div
            className="bg-white rounded-lg shadow-xl max-w-md w-full relative"
            style={{ zIndex: 10000 }}
          >
            <div className="flex items-center justify-between p-6 border-b">
              <h3 className="text-lg font-semibold text-gray-900">
                Beta Feedback
              </h3>
              <button
                onClick={handleCloseFeedbackModal}
                className="text-gray-400 hover:text-gray-600 focus:outline-none"
                aria-label="Close feedback modal"
              >
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>
            <div className="p-6">
              <p className="text-sm text-gray-600 mb-4">
                We'd love to hear your feedback about Chamber Member Pro. What
                can we improve?
              </p>

              {submitMessage && (
                <div
                  className={`mb-4 p-3 rounded-md text-sm ${
                    submitMessage.includes("successfully")
                      ? "bg-green-100 text-green-800 border border-green-200"
                      : "bg-red-100 text-red-800 border border-red-200"
                  }`}
                >
                  {submitMessage}
                </div>
              )}

              <textarea
                value={feedbackText}
                onChange={(e) => setFeedbackText(e.target.value)}
                placeholder="Type your feedback here..."
                disabled={isSubmitting}
                className="w-full h-32 p-3 border border-gray-300 rounded-md resize-none focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed"
              />
            </div>
            <div className="flex items-center justify-end p-6 border-t space-x-3">
              <button
                onClick={handleCloseFeedbackModal}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-300"
              >
                Cancel
              </button>
              <button
                onClick={handleSubmitFeedback}
                disabled={!feedbackText.trim() || isSubmitting}
                className="px-4 py-2 text-sm font-medium text-white rounded-md focus:outline-none focus:ring-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
                style={{
                  backgroundColor: "#E1A731",
                }}
                onMouseEnter={(e) => {
                  if (!e.currentTarget.disabled) {
                    e.currentTarget.style.backgroundColor = "#c78f1e";
                  }
                }}
                onMouseLeave={(e) => {
                  if (!e.currentTarget.disabled) {
                    e.currentTarget.style.backgroundColor = "#E1A731";
                  }
                }}
              >
                {isSubmitting ? "Sending..." : "Send Feedback"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Layout;
